<template>
  <v-container
    fluid
    :style="{height:'100%', 'background-color':pageElements[$route.name]? pageElements[$route.name].color: '' }"
  >
    <task-dialog :openAtStart="false"/>

    <v-layout row wrap>
      <v-flex xs12>
        <v-img
          :src="pageElements[$route.name]?pageElements[$route.name].img:''"
          height="100px"
          contain
        ></v-img>
      </v-flex>

      <v-flex sm12 md9>
        <v-card ma-5 height="100%" min-height="550px">
          <v-card-title>
            <span class="headline" >Enter your text to classify</span>
            <v-spacer></v-spacer>

            <v-flex xs12 sm4 v-if="languages.length>1">
              <v-overflow-btn
                v-model="language"
                :items="languages"
                label="Language"
                target="#language"
                @input="classify"
              ></v-overflow-btn>
            </v-flex>
          </v-card-title>

          <v-card-title v-if="$route.name == 'text_age_verification'">
            <span class="subtitle">Please note that the model requires 3-5 sentences of text in order to make an accurate prediction.</span>
          </v-card-title>

          <v-card-text>
            <v-form v-on:submit.prevent="classify">
              <v-layout row v-if="false">
                <v-flex xs12 md6>
                  <v-checkbox label="Get attributions" v-model="computeAttributions"></v-checkbox>
                </v-flex>
                <v-flex xs12 md6>
                  <v-checkbox label="Add to dataset" v-model="addToDataset"></v-checkbox>
                </v-flex>
              </v-layout>
              <v-text-field
                v-model="text"
                :append-outer-icon="'send'"
                :prepend-icon="icon"
                box
                clear-icon="close"
                clearable
                label="Text"
                type="text"
                @click:append-outer="classify"
                @click:clear="clearText"
                @input="debounceClassify"
              >
                <v-progress-linear
                  v-if="loading"
                  slot="progress"
                  color="warning"
                  :indeterminate="true"
                ></v-progress-linear>

                <v-progress-linear v-else slot="progress" color="success" :value="100" height="7"></v-progress-linear>
              </v-text-field>
            </v-form>
          </v-card-text>

          <v-card-text>
            <v-layout
              v-if="loading"
              slot="placeholder"
              fill-height
              align-center
              justify-center
              ma-0
            >
              <v-progress-circular indeterminate color="grey"></v-progress-circular>
            </v-layout>

            <v-layout column v-else-if="taskData" align-center>
              <v-flex xs12 v-if="$route.name !== 'exp_hate_toxicity'">
                <v-chip
                  outline
                  v-for="(label,classifierName,i) in classificationLabels"
                  :color="classifierColor(classifierName )"
                  :key="i"
                >
                  <v-icon
                    left
                    :color="classifierColor(classifierName )"
                  >{{classifierIcon(classifierName, label)}}</v-icon>
                  {{label}}
                </v-chip>
                <v-chip v-if="false" outline>{{taskData.language}}</v-chip>
              </v-flex>
              <v-flex xs12>
                <task :taskData="taskData" :taskIndex="0" :context="'interact'"></task>
              </v-flex>
            </v-layout>
            <v-layout column v-if="taskData" align-end>
              <v-flex xs12>
                <v-tooltip top>
                  <v-btn
                    slot="activator"
                    dark
                    :color="pageElements[$route.name]?pageElements[$route.name].color:''"
                    @click="sendFeedback"
                  >I Disagree with the prediction</v-btn>
                  <span>Click to report a mistake</span>
                </v-tooltip>
                <v-snackbar
                  color="success"
                  v-model="snackbar"
                  :timeout="2000"
                >Thanks for your feedback!</v-snackbar>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex sm12 md3>
        <v-card ma-5 height="100%">
          <infobox
            :explanation="pageElements[$route.name]?pageElements[$route.name].explanation:''"
            :infos="interactInfos"
          ></infobox>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Task from "@/components/common/Task.vue";
import { mapGetters, mapActions } from "vuex";
import TaskDialog from "@/components/common/TaskDialog.vue";
import _ from "lodash";
import Particles from "@/components/common/Particles.vue";
import Infobox from "@/components/common/Infobox.vue";

export default {
  name: "interact",
  components: { Task, TaskDialog, Particles, Infobox },
  props: {
    // classifierNames: { type: Array, default: null }
  },
  data() {
    return {
      language: "English",

      snackbar: false,
      text: "",
      computeAttributions: false,
      addToDataset: false,
      taskData: null,
      loading: false,
      HATE_THRESHOLD: 0.5,
      TOXICITY_THRESHOLD: 0.5,
      HATE_DE_THRESHOLD: 0.65
    };
  },
  computed: {
    ...mapGetters("labeling", {
      classifiersConfigs: "classifiersConfigs"
    }),
    ...mapGetters("common", {
      pageElements: "pageElements",
      interactInfos: "interactInfos"
    }),
    languages() {
      if (this.$route.name === "hate_toxicity") {
        return ["English", "French", "German - Swiss German", "Swiss German"];
      } else {
        return ["English"];
      }
    },
    classifierNames() {
      if (this.$route.name === "hate_toxicity") {
        if (this.language === "English") {
          return ["profanity_en", "hate", "toxicity"];
        } else if (this.language === "German - Swiss German") {
          return ["de_keyboard_prediction", "profanity_de", "hate_de_trans", "hate_de_all", "hate_de_germeval2019", "hate_ch"];
        } else if (this.language === "Swiss German") {
          return ["profanity_ch-de", "hate_ch"];
        } else if (this.language === "French") {
          return ["profanity_fr", "hate_fr_trans"];
        }
      } else if (this.$route.name === "text_age_verification") {
        if (this.language === "English") {
          return ["text_age_verification"];
        }
      } else if (this.$route.name === "emotion") {
        if (this.language === "English") {
          return ["emotion_ensemble", "sentiment"];
        }
      } else if (this.$route.name === "exp_hate_toxicity") {
        if (this.language === "English") {
          return [
            "unbiased_toxicity",
            "severe_toxicity",
            "obscene",
            "identity_attack",
            "identity",
            "insult",
            "threat",
            "sexual_explicit",
            "aggression"

            // "attack",
            // "toxicity",
            // "hate"
          ];
        }
      }
      else{
        return Object.keys(this.classifiersConfigs)
      }
    },
    classificationLabels() {
      if (this.language === "German - Swiss German") {
        let label = "clean";

        // If length of the input is smaller than 2 words (only counting white spaces) only account for profanity
        if (this.text.split(/(\s+)/).length < 4) {
          let best = _.maxBy(this.taskData.predictions["profanity_de"].preds, function(o) {
              return o.score;
          });
          if (best.label === "profane") {
            label = "hate";
          } else {
            label = "clean";
          }
        } else {
          // Otherwise, let us use the four models and just predict hate if one is above the threshold
          let targetClassifiers = ["hate_de_all", "hate_de_trans", "hate_de_germeval2019", "profanity_de", "hate_ch"];
          for (const classifierName of targetClassifiers) {
            if (classifierName in this.taskData.predictions) {
              let bestScore = _.maxBy(this.taskData.predictions[classifierName].preds, function(o) {
              return o.score;
            });
              if (bestScore.label === "clean") {
                if (bestScore.score < this.HATE_DE_THRESHOLD) {
                  label = "hate";
                  break;
                } else {
                  label = "clean";
                }
              } else {
                label = "hate";
                break;
              }
            }
          }
        }
        
        return { hate_de : label };
        
      } else if (this.language === "Swiss German") {
        let label = "clean";

        // If length of the input is smaller than 2 words (only counting white spaces) only account for profanity
        if (this.text.split(/(\s+)/).length < 4) {
          let best = _.maxBy(this.taskData.predictions["profanity_ch-de"].preds, function(o) {
              return o.score;
          });
          if (best.label === "profane") {
            label = "hate";
          } else {
            label = "clean";
          }
        } else {
          // Otherwise, let us use the four models and just predict hate if one is above the threshold
          let targetClassifiers = ["hate_ch"];
          for (const classifierName of targetClassifiers) {
            if (classifierName in this.taskData.predictions) {
              let bestScore = _.maxBy(this.taskData.predictions[classifierName].preds, function(o) {
              return o.score;
            });
              if (bestScore.label === "clean") {
                if (bestScore.score < this.HATE_DE_THRESHOLD) {
                  label = "hate";
                  break;
                } else {
                  label = "clean";
                }
              } else {
                label = "hate";
                break;
              }
            }
          }
        }
        
        return { hate_ch : label };
      } else {
        let targetClassifiers = ["emotion_ensemble", "hate", "toxicity", "profanity_en"];
        let result = {};
        for (const classifierName of targetClassifiers) {
          if (classifierName in this.taskData.predictions) {
            let best = _.maxBy(this.taskData.predictions[classifierName].preds, function(o) {
              return o.score;
            });
            if (classifierName === "hate") {
              result[classifierName] = (best.label === "hate" && best.score) > this.HATE_THRESHOLD ? "hate" : "clean";
            } else if (classifierName === "toxicity") {
              result[classifierName] =
                (best.label === "toxic" && best.score) > this.TOXICITY_THRESHOLD ? "toxic" : "safe";
            } else {
              result[classifierName] = best.label;
            }
          }
        }
        return result;
      }
    },
    _() {
      return _;
    },
    icon() {
      return "";
    }
  },
  methods: {
    ...mapActions("labeling", {
      addTexts: "addTexts",
      classifyTexts: "classifyTexts",
      setClassifierNames: "setClassifierNames"
    }),
    classifierColor(classifierName) {
      let mapper = {
        emotion_ensemble: "indigo",
        profanity_en: "violet",
        hate: "blue",
        hate_de: "blue",
        hate_ch: "blue",
        toxicity: "brown"
      };
      return mapper[classifierName];
    },
    classifierIcon(classifierName, label) {
      let mapper = {
        emotion_ensemble: {
          anger: "mood_bad",
          fear: "mood_bad",
          joy: "sentiment_satisfied_alt",
          love: "favorite",
          neutral: "face",
          sadness: "sentiment_dissatisfied",
          surprise: "sentiment_very_satisfied"
        },
        hate: { clean: "done", hate: "error" },
        toxicity: { safe: "done", toxic: "warning" },
        profanity_en: { clean: "done", profane: "error" },
        hate_de: { clean: "done", hate: "error" },
        hate_ch: { clean: "done", hate: "error" }
      };
      return mapper[classifierName][label];
    },
    debounceClassify: _.debounce(function() {
      this.classify();
    }, 500),
    clearText() {
      this.text = "";
    },
    sendFeedback() {
      this.addTexts({ texts: [this.text], collection: this.$route.name });
      this.snackbar = true;
    },
    classify() {
      this.setClassifierNames(this.classifierNames);
      let self = this;
      if (!self.text) {
        return;
      }
      self.taskData = null;
      self.loading = true;
      self
        .classifyTexts({
          texts: [self.text],
          computeAttributions: self.computeAttributions
        })
        .then(function(results) {
          self.taskData = results.data.results[0];
          // if we have a set of target classifier names, we filter the task data to that list.
          if (self.classifierNames) {
            let predictions = {};
            for (let classifierName of self.classifierNames) {
              if (classifierName in self.taskData.predictions) {
                predictions[classifierName] = self.taskData.predictions[classifierName];
              }
            }
            self.taskData.predictions = predictions;
          }
          self.loading = false;
          if (self.addToDataset) {
            self.addTexts([self.text]);
          }
        });
    }
  },
  created() {}
};
</script>

<style>
</style>

